import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { fetchMyProfile, saveMyProfile, type UserProfile } from '@/store/slices/profileSlice';
import { fetchAdvertisingTypes, fetchSocialmediaTypes, fetchEnvs } from '@/store/slices/envsSlice';
import { showSpinner, hideSpinner } from '@/store/slices/helpersSlice';
import {
  Save, Mail, Plus, MinusCircle, CheckCircle,
  BarChart2, FileText, ShieldCheck,
} from 'lucide-react';
import { FaPaypal, FaYoutube, FaTwitch, FaTwitter, FaTiktok, FaInstagram, FaDiscord } from 'react-icons/fa';
import { getYoutubeAuthUrl } from '@/lib/utils/authUrls';
import { isDirectAuthMode } from '@/lib/config/apiFlags';

/**
 * 원본: .old-gc-mono/apps/gc-zoku/components/pages/Profile/
 * ProfileForm.js + ProfileProgress.js
 */

// ── Advertising type constants (원본: @gc/common/constants/advTypeRate) ──

interface VariationOption { value: string | number; text: string }
interface VariationConfig {
  name: string;
  options: VariationOption[];
  default: string | number;
  modelPath: string;
}

const ADV_TYPES_WITH_VARIATION: Record<string, VariationConfig> = {
  stream: {
    name: 'hours',
    options: [1, 2, 3, 4].map((h) => ({ value: h, text: `${h} ${h > 1 ? 'hours' : 'hour'}` })),
    default: 2,
    modelPath: 'streamOnly.hours',
  },
  'footage-right': {
    name: 'duration',
    options: [
      { value: '1month', text: '1 month' },
      { value: '3months', text: '3 months' },
      { value: '6months', text: '6 months' },
      { value: '1year', text: '1 year' },
    ],
    default: '3months',
    modelPath: 'footageRightOnly.duration',
  },
};

const DEFAULT_ADV_TYPE_BY_CHANNEL: Record<string, string> = {
  youtube: 'pre-roll', twitch: 'stream', twitter: 'tweet', tiktok: 'tiktok', instagram: 'instagram-post',
};

// ── Social media icon mapping ──

const SM_ICONS: Record<string, React.ReactNode> = {
  youtube: <FaYoutube />,
  twitch: <FaTwitch />,
  twitter: <FaTwitter />,
  tiktok: <FaTiktok />,
  instagram: <FaInstagram />,
};

// ── Types ──

interface AdvType {
  _id?: string;
  id?: string;
  title: string;
  isObsolete?: boolean;
  order?: number;
}

interface SocialMediaType {
  id: string;
  title: string;
  icon?: string;
}

interface RateItem {
  [key: string]: unknown;
  key: string;
  advType: string;
  price: number | string;
  hours?: number;
  duration?: string;
}

// ── Main Component ──

function Profile() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((s) => s.envs.currentUser);
  const profile = useAppSelector((s) => s.profile.myProfile);
  const saving = useAppSelector((s) => s.profile.saving);
  const advertisingTypes = useAppSelector((s) => s.envs.advertisingTypes) as AdvType[] | null;
  const socialmediaTypes = useAppSelector((s) => s.envs.socialmediaTypes) as SocialMediaType[] | null;

  const [email, setEmail] = useState('');
  const [discord, setDiscord] = useState('');
  const [paypal, setPaypal] = useState('');
  const [socialMedia, setSocialMedia] = useState<Record<string, string>>({});
  const [rates, setRates] = useState<RateItem[]>([]);
  const [nextKey, setNextKey] = useState(0);
  const [successMsg, setSuccessMsg] = useState('');

  const validAdvTypes = useMemo(() =>
    (advertisingTypes || []).filter((t) => !t.isObsolete),
  [advertisingTypes]);

  const channelType = useMemo(() => {
    const ch = currentUser?.channels?.[0] as Record<string, unknown> | undefined;
    return (ch?.type as string) || (ch?.channelType as string) || '';
  }, [currentUser]);

  const defaultAdvType = useMemo(() =>
    DEFAULT_ADV_TYPE_BY_CHANNEL[channelType] || 'pre-roll',
  [channelType]);

  useEffect(() => {
    dispatch(showSpinner());
    Promise.all([
      dispatch(fetchMyProfile()),
      dispatch(fetchAdvertisingTypes()),
      dispatch(fetchSocialmediaTypes()),
    ]).finally(() => dispatch(hideSpinner()));
  }, [dispatch]);

  // 프로필 데이터 → 폼 초기화 (contact, payment, social media)
  useEffect(() => {
    if (!profile) return;
    const p = profile as Record<string, unknown>;
    setEmail((p.email as string) || '');
    setDiscord((p.discord as string) || '');
    setPaypal((p.paypal as string) || '');
    setSocialMedia((p.socialmedia as Record<string, string>) || {});
  }, [profile]);

  // ratesV2 초기화 - currentUser에서 읽어옴 (원본과 동일)
  useEffect(() => {
    if (!currentUser) return;
    const cu = currentUser as Record<string, unknown>;
    const ratesV2 = (cu.ratesV2 as { items?: Array<Record<string, unknown>> }) || convertOldRates(cu.rates as Record<string, unknown>);
    const items = ratesV2?.items || [];
    const mapped: RateItem[] = items.map((item, i) => {
      const type = (item.type as string) || '';
      const varConfig = ADV_TYPES_WITH_VARIATION[type];
      return {
        key: `r${i}`,
        advType: type,
        price: (item.price as number) || 0,
        ...(varConfig?.name === 'hours' ? { hours: getNestedValue(item, varConfig.modelPath) as number || varConfig.default as number } : {}),
        ...(varConfig?.name === 'duration' ? { duration: getNestedValue(item, varConfig.modelPath) as string || varConfig.default as string } : {}),
      };
    });
    setRates(mapped);
    setNextKey(items.length);
  }, [currentUser]);

  const addRate = useCallback(() => {
    const usedTypes = rates.map((r) => r.advType);
    // 기본 타입 우선, 미사용 타입 찾기
    const sorted = [...validAdvTypes].sort((a, b) => {
      const aDefault = (a._id || a.id) === defaultAdvType ? 0 : 1;
      const bDefault = (b._id || b.id) === defaultAdvType ? 0 : 1;
      return aDefault - bDefault;
    });

    let nextAdvType: string | null = null;
    let variation: { name: string; value: string | number } | null = null;

    for (const at of sorted) {
      const atId = at._id || at.id || '';
      const varConfig = ADV_TYPES_WITH_VARIATION[atId];

      if (!varConfig) {
        if (!usedTypes.includes(atId)) {
          nextAdvType = atId;
          break;
        }
      } else {
        const usedVariations = rates.filter((r) => r.advType === atId)
          .map((r) => (r as Record<string, unknown>)[varConfig.name]);
        const available = varConfig.options.filter((o) => !usedVariations.includes(o.value));
        if (available.length > 0) {
          nextAdvType = atId;
          const defAvail = available.find((o) => o.value === varConfig.default);
          variation = { name: varConfig.name, value: defAvail ? defAvail.value : available[0].value };
          break;
        }
      }
    }

    if (!nextAdvType) return;

    setRates((prev) => [...prev, {
      key: `r${nextKey}`,
      advType: nextAdvType!,
      price: '',
      ...(variation?.name === 'hours' ? { hours: variation.value as number } : {}),
      ...(variation?.name === 'duration' ? { duration: variation.value as string } : {}),
    }]);
    setNextKey((k) => k + 1);
  }, [rates, validAdvTypes, defaultAdvType, nextKey]);

  const removeRate = useCallback((key: string) => {
    setRates((prev) => prev.filter((r) => r.key !== key));
  }, []);

  const updateRate = useCallback((key: string, field: string, value: unknown) => {
    setRates((prev) => prev.map((r) => r.key === key ? { ...r, [field]: value } : r));
  }, []);

  const handleSave = useCallback(async () => {
    const data: Record<string, unknown> = {
      email,
      discord,
      paypal,
      socialmedia: socialMedia,
      rates: rates.filter((r) => r.price !== '' && r.price !== null).map((r) => {
        const item: Record<string, unknown> = { advType: r.advType, price: Number(r.price) };
        const varConfig = ADV_TYPES_WITH_VARIATION[r.advType];
        if (varConfig) {
          item[varConfig.name] = (r as Record<string, unknown>)[varConfig.name];
        }
        return item;
      }),
    };

    dispatch(showSpinner());
    try {
      await dispatch(saveMyProfile(data as Partial<UserProfile>)).unwrap();
      // 프로필 + currentUser(ratesV2 포함) 동시 갱신
      await Promise.all([dispatch(fetchMyProfile()), dispatch(fetchEnvs())]);
      setSuccessMsg('Saved');
      setTimeout(() => setSuccessMsg(''), 3000);
    } finally {
      dispatch(hideSpinner());
    }
  }, [dispatch, email, discord, paypal, socialMedia, rates]);

  if (!currentUser || !profile || !advertisingTypes || !socialmediaTypes) {
    return null;
  }

  const isYoutube = channelType === 'youtube';
  const grantAnalytic = !!((currentUser as Record<string, unknown>).token as Record<string, unknown>)?.grantAnalytic;
  const hasVariationType = rates.some((r) => !!ADV_TYPES_WITH_VARIATION[r.advType]);

  const canAddRate = (() => {
    for (const at of validAdvTypes) {
      const atId = at._id || at.id || '';
      const varConfig = ADV_TYPES_WITH_VARIATION[atId];
      if (!varConfig) {
        if (!rates.some((r) => r.advType === atId)) return true;
      } else {
        const used = rates.filter((r) => r.advType === atId).map((r) => (r as Record<string, unknown>)[varConfig.name]);
        if (varConfig.options.some((o) => !used.includes(o.value))) return true;
      }
    }
    return false;
  })();

  return (
    <div className="pf-root">
      {/* ── Left: Profile Form ── */}
      <div className="pf-form-card">
        <div className="pf-form-header">
          <h2 className="pf-title">Profile</h2>
          <button onClick={handleSave} disabled={saving} className="pf-save-btn">
            <Save size={14} /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {successMsg && <div className="pf-success-msg">{successMsg}</div>}

        {/* Contact Details */}
        <h3 className="pf-section-title">Contact details</h3>
        <HFormField label="Contact email" required
          icon={<Mail size={14} />}
          value={email} onChange={setEmail} type="email"
        />
        <HFormField label="Discord"
          icon={<FaDiscord />}
          value={discord} onChange={setDiscord}
        />

        {/* Payments */}
        <h3 className="pf-section-title">Payments</h3>
        <HFormField label="Paypal Email" required
          icon={<FaPaypal />}
          value={paypal} onChange={setPaypal} type="email"
        />

        {/* Social Media */}
        <h3 className="pf-section-title">Social media</h3>
        {socialmediaTypes.map((sType) => (
          <HFormField
            key={sType.id}
            label={sType.title}
            icon={SM_ICONS[sType.id] || null}
            value={socialMedia[sType.id] || ''}
            onChange={(v) => setSocialMedia((prev) => ({ ...prev, [sType.id]: v }))}
          />
        ))}

        {/* Advertising Types */}
        <h3 className="pf-section-title">Advertising types</h3>
        {rates.map((rate) => {
          const varConfig = ADV_TYPES_WITH_VARIATION[rate.advType];
          return (
            <div key={rate.key} className="pf-rate-row">
              <select
                className="pf-rate-select"
                value={rate.advType}
                onChange={(e) => updateRate(rate.key, 'advType', e.target.value)}
              >
                {validAdvTypes.map((at) => (
                  <option key={at._id || at.id} value={at._id || at.id}>{at.title}</option>
                ))}
              </select>
              <input
                type="number"
                className="pf-rate-price"
                placeholder="Price"
                value={rate.price}
                onChange={(e) => updateRate(rate.key, 'price', e.target.value)}
              />
              {hasVariationType && varConfig && (
                <select
                  className="pf-rate-variation"
                  value={((rate as Record<string, unknown>)[varConfig.name] as string | number) || ''}
                  onChange={(e) => {
                    const val = varConfig.name === 'hours' ? Number(e.target.value) : e.target.value;
                    updateRate(rate.key, varConfig.name, val);
                  }}
                >
                  {varConfig.options.map((op) => (
                    <option key={String(op.value)} value={op.value}>{op.text}</option>
                  ))}
                </select>
              )}
              {hasVariationType && !varConfig && <div className="pf-rate-variation-spacer" />}
              <button className="pf-rate-remove" onClick={() => removeRate(rate.key)} title="Remove">
                <MinusCircle size={20} />
              </button>
            </div>
          );
        })}
        {canAddRate && (
          <button className="pf-add-rate" onClick={addRate}>
            <Plus size={14} /> Add rate
          </button>
        )}
      </div>

      {/* ── Right: Profile Progress ── */}
      <div className="pf-progress-col">
        <div className="pf-progress-card">
          <ProgressItem label="Contact Email Added" done={!!profile.email} />
          <ProgressItem label="PayPal Email Added" done={!!(profile as Record<string, unknown>).paypal} />
          {isYoutube && (
            <div className="pf-progress-item">
              <ProgressDot done={grantAnalytic} />
              <div>
                <span className={grantAnalytic ? 'pf-done' : 'pf-pending'}>Channel Analytics Granted</span>
                {!grantAnalytic && (
                  <div className="pf-grant-help">
                    <p className="pf-grant-info">
                      Please click here to grant us permission to view your YouTube analytics.
                    </p>
                    <a href={getYoutubeAuthUrl({ grantAnalytic: true, direct: isDirectAuthMode() })} className="pf-grant-btn">
                      <BarChart2 size={12} /> Grant Analytics
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
          <ProgressItem
            label="Profile Complete!"
            done={!!profile.email && !!(profile as Record<string, unknown>).paypal && (!isYoutube || grantAnalytic)}
          />
        </div>

        <div className="pf-links">
          <a href="/terms_and_conditions/influencer" target="_blank" rel="noopener noreferrer" className="pf-link-btn">
            <FileText size={12} /> Terms of Use
          </a>
          <a href="/terms_and_conditions/privacy" target="_blank" rel="noopener noreferrer" className="pf-link-btn">
            <ShieldCheck size={12} /> Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
}

// ── Horizontal Form Field (label : [icon] input) ──

interface HFormFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  icon?: React.ReactNode;
}

function HFormField({ label, value, onChange, type = 'text', required, icon }: HFormFieldProps) {
  return (
    <div className="pf-hfield">
      <label className="pf-hlabel">
        {required && <span className="pf-required">*</span>}
        {label} :
      </label>
      <div className="pf-hinput-wrap">
        {icon && <span className="pf-hinput-icon">{icon}</span>}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`pf-hinput ${icon ? 'pf-hinput-with-icon' : ''}`}
        />
      </div>
    </div>
  );
}

// ── Progress Components ──

function ProgressDot({ done }: { done: boolean }) {
  return done
    ? <CheckCircle size={16} className="pf-check-done" />
    : <span className="pf-check-pending" />;
}

function ProgressItem({ label, done }: { label: string; done: boolean }) {
  return (
    <div className="pf-progress-item">
      <ProgressDot done={done} />
      <span className={done ? 'pf-done' : 'pf-pending'}>{label}</span>
    </div>
  );
}

// ── Utilities ──

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((acc: unknown, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
}

function convertOldRates(rates: Record<string, unknown> | undefined): { items: Array<Record<string, unknown>> } | null {
  if (!rates) return null;
  const data = rates.data as Record<string, number> | undefined;
  if (!data) return null;
  const items: Array<Record<string, unknown>> = [];
  for (const [advType, price] of Object.entries(data)) {
    if (typeof price !== 'number' || price <= 0) continue;
    items.push({ type: advType, price });
  }
  return { items };
}

export default Profile;
